import {
	faExclamationTriangle,
	faPencilAlt,
	faPlus,
	faSpinner,
	faTrash
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { apiClient } from "@/api/axiosInstance";
import { API_ENDPOINTS, resolveEndpoint } from "@/api/endpoints";
import { useToast } from "@/components/common/Toast";
import SidePanel from "@/components/Sidepanel";
import NotFound from "@/pages/common/NotFound";
import { FlowAction } from "@/store/flowActions.store";
import { FlowType } from "@/store/flowTypes.store";
import { formatDateWithTime } from "@/utils/dateFormatter";
import { ApiError, handleApiError } from "@/utils/errorHandler";
import AddFlowAction from "./AddFlowAction";
import ViewFlowTraget from "./ViewFlowTraget";

// Define query keys as constants
const FLOW_TYPES_QUERY_KEY = ["flow-types"] as const;
const FLOW_ACTIONS_QUERY_KEY = ["flow-actions"] as const;

const ViewFlowAction = () => {
	const { success } = useToast();
	const { flowTypeId } = useParams();
	const queryClient = useQueryClient();

	// UI state
	const [isEditPanelOpen, setIsEditPanelOpen] = useState(false);
	const [isCreatePanelOpen, setIsCreatePanelOpen] = useState(false);
	const [selectedAction, setSelectedAction] = useState<{
		id: string;
		name: string;
		steps: string[];
		inputSchema: Record<string, unknown>;
		outputSchema: Record<string, unknown>;
	} | null>(null);
	const [itemToDelete, setItemToDelete] = useState<{
		id: string;
		type: "action";
		name: string;
	} | null>(null);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

	// Fetch flow types
	const flowTypesQuery = useQuery({
		queryKey: FLOW_TYPES_QUERY_KEY,
		queryFn: async () => {
			const { data } = await apiClient.get(
				resolveEndpoint(API_ENDPOINTS, ["FLOW_TYPES", "LIST"])
			);
			return data;
		}
	});

	// Fetch actions for the selected flow type
	const flowActionsQuery = useQuery({
		queryKey: [...FLOW_ACTIONS_QUERY_KEY, flowTypeId],
		queryFn: async () => {
			if (!flowTypeId) return [];
			const { data } = await apiClient.get(
				resolveEndpoint(API_ENDPOINTS, ["FLOW_ACTIONS", "LIST"], flowTypeId)
			);
			return data;
		},
		enabled: !!flowTypeId
	});

	// Get flow type from query data
	const flowType = flowTypesQuery.data?.find(
		(ft: FlowType) => ft.id === flowTypeId
	);

	// Delete mutation
	const deleteMutation = useMutation({
		mutationFn: async (actionId: string) => {
			if (!flowType) throw new Error("Flow type not found");
			return apiClient.delete(
				resolveEndpoint(
					API_ENDPOINTS,
					["FLOW_ACTIONS", "DELETE_BY_ID"],
					flowType.id,
					actionId
				)
			);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [...FLOW_ACTIONS_QUERY_KEY, flowTypeId]
			});
			success("Action deleted successfully");
			setIsDeleteModalOpen(false);
			setItemToDelete(null);
		},
		onError: (error: ApiError) => {
			handleApiError(error, "Failed to delete action");
		}
	});

	// Get action details mutation
	const getActionDetailsMutation = useMutation({
		mutationFn: async (actionId: string) => {
			if (!flowType) throw new Error("Flow type not found");
			const { data } = await apiClient.get(
				resolveEndpoint(
					API_ENDPOINTS,
					["FLOW_ACTIONS", "GET_BY_ID"],
					flowType.id,
					actionId
				)
			);
			return data;
		},
		onSuccess: (data) => {
			setSelectedAction({
				id: data.id,
				name: data.name,
				steps: data.steps || [],
				inputSchema: data.inputSchema || {},
				outputSchema: data.outputSchema || {}
			});
			setIsEditPanelOpen(true);
		},
		onError: (error: ApiError) => {
			handleApiError(error, "Failed to get action details");
		}
	});

	const handleEditAction = (actionId: string) => {
		getActionDetailsMutation.mutate(actionId);
	};

	const handleDeleteClick = (id: string, type: "action", name: string) => {
		setItemToDelete({ id, type, name });
		setIsDeleteModalOpen(true);
	};

	const handleConfirmDelete = () => {
		if (!itemToDelete) return;
		deleteMutation.mutate(itemToDelete.id);
	};

	// Show loading while fetching initial data
	if (flowTypesQuery.isLoading || flowActionsQuery.isLoading) {
		return (
			<div className="flex justify-center items-center h-screen">
				<FontAwesomeIcon
					icon={faSpinner}
					spin
					className="h-8 w-8 text-blue-500"
				/>
			</div>
		);
	}

	// Show not found only if flow types are loaded and ID is invalid
	if (flowTypesQuery.data && !flowType) {
		return <NotFound />;
	}

	return (
		<div className="flex flex-col">
			<div className="flex-1">
				<div className="space-y-6">
					<div className="bg-white shadow rounded-lg border">
						<div>
							<div className="border-b flex items-center justify-between mb-4 p-4 bg-blue-50">
								<h2 className="text-xl font-semibold text-gray-900">Actions</h2>
								<button
									type="button"
									onClick={() => setIsCreatePanelOpen(true)}
									className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-fynxt hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
								>
									<FontAwesomeIcon icon={faPlus} className="mr-2" />
									Create Action
								</button>
							</div>
							<div className="grid gap-4 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 p-4">
								{flowActionsQuery.data && flowActionsQuery.data.length > 0 ? (
									flowActionsQuery.data.map((action: FlowAction) => (
										<div
											key={action.id}
											className="group relative bg-white border border-gray-200 rounded-lg transition-shadow duration-300 hover:shadow-md overflow-hidden"
										>
											<div className="absolute inset-0 bg-gray-300 opacity-0 group-hover:opacity-40 transition-opacity duration-300 pointer-events-none z-0" />

											<div className="relative z-10 p-4 flex items-center justify-between bg-gray-50">
												<div>
													<h3 className="text-lg font-semibold text-gray-800 text-left">
														{action.name}
													</h3>
													<p className="text-sm text-gray-500 mt-1 text-left">
														Created At: {formatDateWithTime(action.createdAt)}
													</p>
												</div>
												<div className="flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
													<button
														type="button"
														className="bg-gray-300 text-green-600 hover:text-green-800 p-2"
														title="Edit"
														onClick={() => handleEditAction(action.id)}
													>
														<FontAwesomeIcon icon={faPencilAlt} />
													</button>
													<button
														type="button"
														className="bg-gray-300 text-red-600 hover:text-red-800 p-2"
														title="Delete"
														onClick={() =>
															handleDeleteClick(
																action.id,
																"action",
																action.name
															)
														}
													>
														<FontAwesomeIcon icon={faTrash} />
													</button>
												</div>
											</div>
										</div>
									))
								) : (
									<div className="col-span-full text-center text-gray-400 text-sm">
										No actions found
									</div>
								)}
							</div>
						</div>
					</div>

					{isEditPanelOpen && selectedAction && (
						<SidePanel
							isOpen={isEditPanelOpen}
							onClose={() => setIsEditPanelOpen(false)}
							title="Edit Action"
							closeOnOutsideClick={false}
						>
							<AddFlowAction
								flowAction={selectedAction}
								flowTypeId={flowTypeId || ""}
								onClose={() => setIsEditPanelOpen(false)}
							/>
						</SidePanel>
					)}

					{isCreatePanelOpen && (
						<SidePanel
							isOpen={isCreatePanelOpen}
							onClose={() => setIsCreatePanelOpen(false)}
							title="Create Action"
							closeOnOutsideClick={false}
						>
							<AddFlowAction
								flowAction={null}
								flowTypeId={flowTypeId || ""}
								onClose={() => setIsCreatePanelOpen(false)}
							/>
						</SidePanel>
					)}

					{isDeleteModalOpen && itemToDelete && (
						<div className="fixed inset-0 z-50 overflow-y-auto">
							<div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
								<div
									className="fixed inset-0 transition-opacity"
									aria-hidden="true"
								>
									<div className="absolute inset-0 bg-gray-500 opacity-75" />
								</div>

								<div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
									<div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
										<div className="sm:flex sm:items-start">
											<div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
												<FontAwesomeIcon
													icon={faExclamationTriangle}
													className="h-6 w-6 text-red-600"
												/>
											</div>
											<div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
												<h3 className="text-lg leading-6 font-medium text-gray-900">
													Delete Action
												</h3>
												<div className="mt-2">
													<p className="text-sm text-gray-500">
														Are you sure you want to delete {itemToDelete?.name}
														? This action cannot be undone.
													</p>
												</div>
											</div>
										</div>
									</div>
									<div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
										<button
											type="button"
											className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
											onClick={handleConfirmDelete}
											disabled={deleteMutation.isPending}
										>
											{deleteMutation.isPending ? (
												<>
													<FontAwesomeIcon
														icon={faSpinner}
														spin
														className="mr-2"
													/>
													Deleting...
												</>
											) : (
												"Delete"
											)}
										</button>
										<button
											type="button"
											className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
											onClick={() => setIsDeleteModalOpen(false)}
											disabled={deleteMutation.isPending}
										>
											Cancel
										</button>
									</div>
								</div>
							</div>
						</div>
					)}

					<div className="space-y-6">
						<div>
							<ViewFlowTraget flowType={flowType} />
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default ViewFlowAction;
